import { Dispatch, SetStateAction } from "react";
import {
  API_URL,
  v,
  put,
  debug,
  request,
  takeEvery,
  createDoRequestAction,
  assertTotalSchema,
  SagaIterator,
  call,
  RequestResult,
} from "../../lib";

import { doREQUEST_ERROR, doREQUEST_COMPLETE, doMETHOD_UPDATE_TASK } from "../state";
import { getToken } from "./token_fetcher";

export const REQUEST_TASK_STOPWATCH_MOVESENSE_STREAM_UPDATE =
  "REQUEST_TASK_STOPWATCH_MOVESENSE_STREAM_UPDATE";
export const doREQUEST_TASK_STOPWATCH_MOVESENSE_STREAM_UPDATE = createDoRequestAction(
  REQUEST_TASK_STOPWATCH_MOVESENSE_STREAM_UPDATE,
);

export const requestTaskStopwatchMovesenseStreamUpdateActionCreators = {
  doREQUEST_TASK_STOPWATCH_MOVESENSE_STREAM_UPDATE,
};

interface Payload {
  payload: {
    requestId: string;
    taskId: string;
    color: string;
    scheduleRepeat: boolean;
    scheduleDescription: string;
    reminderList?: Record<string, string>[];
    name: string;
    description: string;
    imuEnable: boolean;
    imuHz: number;
    accEnable: boolean;
    accHz: number;
    gyroscopeEnable: boolean;
    gyroscopeHz: number;
    magnetometerEnable: boolean;
    magnetometerHz: number;
    ecgEnable: boolean;
    ecgHz: number;
    rrEnable: boolean;
    tempTaskId: string;
    timeLimitSeconds: number;
  };
  setRequestResult?: Dispatch<SetStateAction<RequestResult | null>>;
}

export function* requestTaskStopwatchMovesenseStreamUpdate({
  payload,
  setRequestResult,
}: Payload): SagaIterator {
  debug("saga*requestTaskStopwatchMovesenseStreamUpdate");

  try {
    assertTotalSchema({
      payload,
      schema: v.object({
        requestId: v.string().uuid().exist(),
        taskId: v.string().uuid().exist(),
        color: v.string().exist(),
        scheduleRepeat: v.boolean(),
        scheduleDescription: v.string().optional(),
        reminderList: v.array().optional(),
        name: v.string().exist(),
        description: v.string().optional(),
        imuEnable: v.boolean().optional(),
        imuHz: v.number().valid(13, 26, 52, 104, 208).optional().allow(null),
        accEnable: v.boolean().optional(),
        accHz: v.number().valid(13, 26, 52, 104, 208).optional().allow(null),
        gyroscopeEnable: v.boolean().optional(),
        gyroscopeHz: v.number().valid(13, 26, 52, 104, 208).optional().allow(null),
        magnetometerEnable: v.boolean().optional(),
        magnetometerHz: v.number().valid(13, 26, 52, 104, 208).optional().allow(null),
        ecgEnable: v.boolean().optional(),
        ecgHz: v.number().valid(125, 128, 200, 250, 256, 500, 512).optional().allow(null),
        rrEnable: v.boolean().optional(),
        timeLimitSeconds: v.number().required(),
      }),
    });
  } catch (error) {
    return yield put(
      doREQUEST_ERROR(
        {
          fromType: REQUEST_TASK_STOPWATCH_MOVESENSE_STREAM_UPDATE,
          requestId: payload.requestId,
          error,
        },
        setRequestResult,
      ),
    );
  }

  const {
    taskId,
    color,
    scheduleRepeat,
    scheduleDescription,
    reminderList,
    name,
    description,
    imuEnable,
    imuHz,
    accEnable,
    accHz,
    gyroscopeEnable,
    gyroscopeHz,
    magnetometerEnable,
    magnetometerHz,
    ecgEnable,
    ecgHz,
    rrEnable,
    timeLimitSeconds,
  } = payload;

  const accessToken = yield call(getToken);

  const result = yield request({
    method: "post",
    url: `${API_URL}/v1/web/task-stopwatch-movesense-stream-update`,
    accessToken,
    data: {
      taskId,
      color,
      scheduleRepeat,
      scheduleDescription,
      reminderList,
      name,
      description,
      imuEnable,
      imuHz,
      accEnable,
      accHz,
      gyroscopeEnable,
      gyroscopeHz,
      magnetometerEnable,
      magnetometerHz,
      ecgEnable,
      ecgHz,
      rrEnable,
      timeLimitSeconds,
    },
  });

  if (!result.success || result.error) {
    const error = result.error || new Error("request did not succeed");
    debug(error);
    return yield put(
      doREQUEST_ERROR(
        {
          fromType: REQUEST_TASK_STOPWATCH_MOVESENSE_STREAM_UPDATE,
          fromPayload: payload,
          requestId: payload.requestId,
          url: "/v1/web/task-stopwatch-movesense-stream-update",
          ...result,
          error,
        },
        setRequestResult,
      ),
    );
  }

  delete result.payload.enabled;
  yield put(doMETHOD_UPDATE_TASK(result.payload));

  yield put(
    doREQUEST_COMPLETE(
      {
        fromType: REQUEST_TASK_STOPWATCH_MOVESENSE_STREAM_UPDATE,
        requestId: payload.requestId,
        ...result,
      },
      setRequestResult,
    ),
  );
}

export function* taskStopwatchMovesenseStreamUpdateSaga() {
  yield takeEvery(
    REQUEST_TASK_STOPWATCH_MOVESENSE_STREAM_UPDATE as any,
    requestTaskStopwatchMovesenseStreamUpdate,
  );
}
